import { useCallback, useEffect, useState } from 'react'
import {
  clearSession,
  consumeSessionFromUrl,
  fetchCurrentEmail,
  readSession,
  signOutSession,
  SupabaseError,
  writeSession,
} from '../lib/supabase'

type SessionStatus = 'checking' | 'signedOut' | 'signedIn'

function friendlyLinkError(raw: string): string {
  if (/expired|invalid/i.test(raw)) {
    return 'That sign-in link has expired or was already used. Send yourself a new one below.'
  }
  return 'That sign-in link didn’t work. Send yourself a new one below.'
}

export function useAdminSession() {
  const [status, setStatus] = useState<SessionStatus>('checking')
  const [email, setEmail] = useState<string | null>(null)
  const [linkError, setLinkError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    const check = async () => {
      const { error } = consumeSessionFromUrl()
      if (error) setLinkError(friendlyLinkError(error))

      const session = readSession()
      if (!session) {
        if (active) setStatus('signedOut')
        return
      }

      try {
        const currentEmail = await fetchCurrentEmail()
        const latest = readSession()
        if (latest) writeSession({ ...latest, email: currentEmail })
        if (!active) return
        setEmail(currentEmail)
        setStatus('signedIn')
      } catch (caught) {
        if (!active) return
        if (caught instanceof SupabaseError && (caught.status === 401 || caught.status === 403)) {
          clearSession()
          setStatus('signedOut')
          return
        }
        // Offline or a hiccup — trust the stored session; writes will re-check.
        setEmail(session.email)
        setStatus('signedIn')
      }
    }

    check()
    return () => {
      active = false
    }
  }, [])

  const signOut = useCallback(async () => {
    await signOutSession()
    setEmail(null)
    setStatus('signedOut')
  }, [])

  return { status, email, linkError, signOut }
}
